import { Options, Vue } from "vue-class-component";
import { SessionStorageEnum } from "@/utils/SessionStorageEnum";
import { UsersRoutesEnum } from "../../router";
import { User } from "@/model/api/User";
import { FilterMatchMode } from "primevue/api";
import { wrsService } from "@services/wrs.service";
import {AppTable, CompanyAutocomplete, DeductDialog, TicketStatus} from "@components";
import WRSsFilters from "./WRSsFilters.vue";
import { WRSFilters } from "./WRSFilters";
import { Shift } from "@/model/api/Shift";
import { TicketStatusEnum } from "@/model/enums/TicketStatusEnum";
import { Ticket } from "@/model/api/Ticket";
import { ticketsService } from "@services/tickets.service";
import { ShiftsRoutesEnum } from "@/modules/shifts/router";
import { TicketsRoutesEnum } from "@/modules/tickets/router";
import {authStore} from "@/modules/auth/store";
import {UserRoleEnum} from "@/model/enums/UserRoleEnum";
import {Company} from "@/model/api/Company";
import {PaymentStatusEnum} from "@/model/enums/PaymentStatusEnum";

enum DriverTicketStatus {
    ACCEPTED,
    ACCEPTABLE,
    UNACCEPTABLE,
}

@Options({
    name: "WrsPage",

    components: {
        AppTable,
        WRSsFilters,
        CompanyAutocomplete,
        DeductDialog,
        TicketStatus
    },

    beforeRouteEnter(to, from, next) {
        if (from.name && !(from.name as string).startsWith(UsersRoutesEnum.WRS_PREFIX)) {
            sessionStorage.removeItem(SessionStorageEnum.TABLE_STATE_WRS);
        }

        next();
    },
    beforeRouteLeave() {

        if (wrsService?.cancelPendingRequests) {

            wrsService.cancelPendingRequests();
        }
    },
})
export default class WRSList extends Vue {
    displayDeductDialog: boolean = false;
    filters: any = null;
    dataset: Shift[] = null;
    brokerDrivers: User[] = null;
    brokerSelected: Company = null;
    selectedDriver: any = null;

    // filtri/ordinamento sulle colonne (i driver del broker selezionato)
    selectedDriverIds: number[] = [];
    driverSort: string = 'name';
    onlyWithActivity: boolean = false;

    get service() {
        return wrsService;
    }

    get driverSortOptions() {
        return [
            { label: 'Sort: name', value: 'name' },
            { label: 'Sort: company', value: 'company' },
            { label: 'Sort: activity', value: 'activity' },
        ];
    }

    get driverOptions() {
        return (this.brokerDrivers ?? [])
            .map((driver: any) => ({
                id: driver.id,
                label: driver.company_name
                    ? `${driver.fullname} - ${driver.company_name}`
                    : driver.fullname
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }

    /** quanti ticket ha ciascun driver sui WRS attualmente caricati */
    get driverActivity(): Record<number, number> {
        const counts: Record<number, number> = {};

        (this.dataset ?? []).forEach((shift: any) => {
            (shift.drivers ?? []).forEach((cell: any) => {
                if (cell?.ticketId != null) {
                    counts[cell.id] = (counts[cell.id] ?? 0) + 1;
                }
            });
        });

        return counts;
    }

    get visibleDrivers(): User[] {
        if (!this.brokerDrivers) return [];

        const needsActivity = this.onlyWithActivity || this.driverSort === 'activity';
        const activity = needsActivity ? this.driverActivity : {};
        let drivers = [...this.brokerDrivers];

        if (this.selectedDriverIds.length) {
            drivers = drivers.filter((d) => this.selectedDriverIds.includes(d.id));
        }

        if (this.onlyWithActivity) {
            drivers = drivers.filter((d) => activity[d.id] > 0);
        }

        const byName = (a: any, b: any) => (a.fullname ?? '').localeCompare(b.fullname ?? '');

        switch (this.driverSort) {
            case 'company':
                drivers.sort((a: any, b: any) =>
                    (a.company_name ?? '').localeCompare(b.company_name ?? '') || byName(a, b));
                break;
            case 'activity':
                drivers.sort((a: any, b: any) =>
                    ((activity[b.id] ?? 0) - (activity[a.id] ?? 0)) || byName(a, b));
                break;
            default:
                drivers.sort(byName);
        }

        return drivers;
    }

    clearDriverFilters() {
        this.selectedDriverIds = [];
        this.onlyWithActivity = false;
        this.driverSort = 'name';
    }

    get hasDriverFilters() {
        return !!this.selectedDriverIds.length || this.onlyWithActivity || this.driverSort !== 'name';
    }

    get stateKey() {
        return SessionStorageEnum.TABLE_STATE_WRS
    }

    get shiftRoute() {
        return ShiftsRoutesEnum.SHIFTS_DETAIL;
    }

    get ticketRoute() {
        return TicketsRoutesEnum.TICKETS_DETAIL;
    }

    get imAdmin() {
        return authStore.getters.me.role === UserRoleEnum.SUPER_ADMIN || authStore.getters.me.role === UserRoleEnum.CLIENT || authStore.getters.me.role === UserRoleEnum.CUSTOMER
    }

    advFilters: WRSFilters = new WRSFilters();

    onReset() {
        this.advFilters = new WRSFilters();
    }

    async updateEnableSelfAccept(driver){
        driver.isLoading = true
        await wrsService.updateEnableSelfAccept(driver.id, driver.enable_self_accept);
        driver.isLoading = false
    }

    onBrokerSelected(){
        sessionStorage.setItem(
            'WRS_broker',
            JSON.stringify(this.brokerSelected)
        );
        this.initData()
    }

    openModal(currentDriver: any){
        this.selectedDriver = currentDriver
        this.displayDeductDialog = true
    }

    updateTicket($event){
        this.selectedDriver.broker_deduct = $event.broker_deduct
        this.selectedDriver.broker_deduct_note = $event.broker_deduct_note
    }

    formatDate() {
        return `${this.advFilters.shift_day.getFullYear()}-${this.advFilters.shift_day.getMonth() + 1}-${this.advFilters.shift_day.getDate()}`
    }

    onFiltersChange() {
        this.filters.shift_day.value = this.formatDate()
        this.loadData();
    }

    private initFilter() {
        this.filters = {
            shift_day: {
                value: this.formatDate(),
                matchMode: FilterMatchMode.EQUALS
            }
        }
    }

    async acceptTicket(shift_id, driver_id) {
        this.requestPending = true
        try {
            await wrsService.setStatusDriver({
                shift_id,
                driver_id,
                broker_id: this.brokerSelected ? this.brokerSelected.id : null,
                status: 20
            });
            await this.loadData();

            this.$successMessage('Ticket accpted successfully')
        } catch (error) {
            this.$errorMessage(error.data.message)
        } finally {
            this.requestPending = false
        }

    }

    deleteTicket(ticketId: number, driverId: number) {
        this.$confirmMessage("Are you sure to delete this Ticket?")
            .then(r => {
                if (r) {
                    this.$waitFor(
                        async () => {
                            await ticketsService.deleteStandard({id: ticketId})
                            await this.loadData();

                            this.$successMessage('Ticket deleted successfully')
                        })
                }
            })
    }

    get DriverTicketStatus() {
        return DriverTicketStatus
    }

    get PaymentStatusEnum(){
        return PaymentStatusEnum
    }

    loadData() {
        this.filters.shift_day.value = this.formatDate()
        return this.$waitFor(
            async () => {
                const dataset = await wrsService.getShiftsByBroker({
                    filters: this.filters,
                    broker_id: this.brokerSelected ? this.brokerSelected.id : null
                });
                this.dataset = dataset.map((shift: any) => {
                    shift.drivers = this.brokerDrivers.map((driver: any) => {
                        const payload:any = {
                            id: driver.id
                        }
                        const currentTicket = shift.tickets.find((ticket) => ticket.user_id === driver.id)
                        if(currentTicket){
                            payload.ticketId = currentTicket.id;
                            payload.ticketStatus = currentTicket.status;
                            payload.broker_deduct = currentTicket.broker_deduct;
                            payload.broker_deduct_note = currentTicket.broker_deduct_note;
                            payload.ticket_payment_status = currentTicket.payment_status;
                            if(currentTicket.status === TicketStatusEnum.DELETED_BY_USER ||
                                currentTicket.status === TicketStatusEnum.ACCEPTED_BY_USER ||
                                currentTicket.status === TicketStatusEnum.DELETED_BY_SYSTEM)
                            {
                                payload.status = DriverTicketStatus.ACCEPTED
                            } else {
                                payload.status = DriverTicketStatus.UNACCEPTABLE

                            }
                        } else {
                            payload.status = DriverTicketStatus.ACCEPTABLE
                        }
                        return payload
                    })
                    // le celle vanno risolte per id: filtrando le colonne l'indice non regge piu'
                    shift.driversById = {}
                    shift.drivers.forEach((cell: any) => {
                        shift.driversById[cell.id] = cell
                    })
                    return shift
                })
            }
        )
    }

    async initData(){
        this.initFilter();
        this.clearDriverFilters();
        // i WRS gia' a video sono dell'altro broker: i loro id driver non esistono piu'
        this.dataset = null;

        await this.$waitFor(async () => {
                this.brokerDrivers = await wrsService.getBrokerDrivers({
                    broker_id: this.brokerSelected ? this.brokerSelected.id : null
                });
            }
        )

        await this.loadData();
    }

    async mounted() {
        if(this.imAdmin){
            if(!sessionStorage.getItem('WRS_broker')){
                return
            }
            this.brokerSelected = JSON.parse(sessionStorage.getItem('WRS_broker'))
        }
        await this.initData()
    }

}
